import React, { useContext, useRef, useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import { TileProps } from "@app/shared/interfaces/dashboard";
import { FontAwesome } from "@app/shared/icons/qesIcons";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { BreadcrumbTemplateEnum } from "@app/shared/breadcrumbs/breadcrumbTemplates";
import Breadcrumbs from "@app/shared/breadcrumbs/Breadcrumbs";
import Page from "@app/shared/Page";
import PageHeader from "@app/shared/PageHeader";
import PageInner from "@app/shared/PageInner";
import PageSection from "@app/shared/PageSection";
import ProjectService from "@app/projects/projectService";
import { UserContext } from "@app/user/UserContext";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";
import BenchmarkingService from "@app/overviews/network-benchmarking-dashboard/benchmarkingService";
import BenchmarkingQHPService from "@app/overviews/network-benchmarking-dashboard/benchmarkingQHPService";
import BenchmarkingMedicaidService from "@app/overviews/network-benchmarking-dashboard/benchmarkingMedicaidService";
import ProviderSetService from "@app/provider-sets/providerSetService";
import ClientService from "@app/clients/clientService";

export const InternalReports: React.FC<React.PropsWithChildren<unknown>> = () => {
  const [exportingProjectConfigs, setExportingProjectConfigs] = useState(false);
  const [exportingProviderNetworkReport, setExportingProviderNetworkReport] = useState(false);
  const [exportingCountyStatusReport, setExportingCountyStatusReport] = useState(false);
  const [exportingBenchmarkingReport, setExportingBenchmarkingReport] = useState(false);
  const [exportingMarketplaceCoverage, setExportingMarketplaceCoverage] = useState(false);
  const [exportingMedicaidCoverage, setExportingMedicaidCoverage] = useState(false);
  const [exportingQasUsersByActiveClient, setExportingQasUsersByActiveClient] = useState(false);
  const [exportingInactiveClientsActiveLicenses, setExportingInactiveClientsActiveLicenses] = useState(false);
  const [exportingAccuracyAttestationLastRun, setExportingAccuracyAttestationLastRun] = useState(false);
  const [exportingClientHealthMetrics, setExportingClientHealthMetrics] = useState(false);
  const [exportingQuinnUtilization, setExportingQuinnUtilization] = useState(false);


  const projectService = new ProjectService();
  const providerSetService = new ProviderSetService();
  const benchmarkingService = new BenchmarkingService();
  const benchmarkingQHPService = useRef(new BenchmarkingQHPService()).current;
  const benchmarkingMedicaidService = useRef(new BenchmarkingMedicaidService()).current;
  const clientService = new ClientService();

  const userContext = useContext(UserContext);

  const handleExportProjectConfigs = (): void => {
    if (!exportingProjectConfigs) {
      setExportingProjectConfigs(true);

      projectService.getProjectDetailsCsv(
        `Project Configurations(${(userContext.roles.includes(Roles.QUEST_ADMIN) || (userContext.roles.includes(Roles.QUEST_INTERNAL_REPORTS) && userContext.clientName === "Quest Analytics")) ? "All" : userContext.clientName})_${moment.utc().local().format("yyyyMMDD_hhmmss")}`,
        (!userContext.roles.includes(Roles.QUEST_ADMIN) && (!userContext.roles.includes(Roles.QUEST_INTERNAL_REPORTS) || userContext.clientName !== "Quest Analytics")) ? userContext.clientId : null
      ).then(() => {
        setExportingProjectConfigs(false);
      });
    }
  }

  const handleExportProviderNetworkConfigs = (): void => {
    if (!exportingProviderNetworkReport) {
      setExportingProviderNetworkReport(true);

      providerSetService.getProviderSetNetworkTaxonomyReport(
        `Provider Network Configuration(${(userContext.roles.includes(Roles.QUEST_ADMIN) || (userContext.roles.includes(Roles.QUEST_INTERNAL_REPORTS) && userContext.clientName === "Quest Analytics")) ? "All" : userContext.clientName})_${moment.utc().local().format("yyyyMMDD_hhmmss")}`,
        (!userContext.roles.includes(Roles.QUEST_ADMIN) && (!userContext.roles.includes(Roles.QUEST_INTERNAL_REPORTS) || userContext.clientName !== "Quest Analytics")) ? userContext.clientId : null
      ).then(() => {
        setExportingProviderNetworkReport(false);
      });
    }
  }

  const handleExportCountyStatuses = (): void => {
    if (!exportingCountyStatusReport) {
      setExportingCountyStatusReport(true);

      projectService.getProjectCountyStatusesCsv(
        `Project County Statuses(${(userContext.roles.includes(Roles.QUEST_ADMIN) || (userContext.roles.includes(Roles.QUEST_INTERNAL_REPORTS) && userContext.clientName === "Quest Analytics")) ? "All" : userContext.clientName})_${moment.utc().local().format("yyyyMMDD_hhmmss")}`,
        (!userContext.roles.includes(Roles.QUEST_ADMIN) && (!userContext.roles.includes(Roles.QUEST_INTERNAL_REPORTS) || userContext.clientName !== "Quest Analytics")) ? userContext.clientId : null
      ).then(() => {
        setExportingCountyStatusReport(false);
      });
    }
  }

  const handleExportQasUsersByActiveClient = (): void => {
    if (!exportingQasUsersByActiveClient) {
      setExportingQasUsersByActiveClient(true);
      clientService.getQasUsersByActiveClientCsv(
        `QAS Users by Active Client_${moment.utc().local().format("yyyyMMDD_hhmmss")}`
      ).then(() => {
        setExportingQasUsersByActiveClient(false);
      });
    }
  }

  const handleExportInactiveClientsActiveLicenses = (): void => {
    if (!exportingInactiveClientsActiveLicenses) {
      setExportingInactiveClientsActiveLicenses(true);
      clientService.getInactiveClientsActiveLicensesCsv(
        `Inactive QES Clients with Active QAS Software Licenses_${moment.utc().local().format("yyyyMMDD_hhmmss")}`
      ).then(() => {
        setExportingInactiveClientsActiveLicenses(false);
      });
    }
  }

  const handleExportAccuracyAttestationLastRun = (): void => {
    if (!userContext.roles.includes(Roles.QUEST_ADMIN) && userContext.clientName !== "Quest Analytics") return;
    if (!exportingAccuracyAttestationLastRun) {
      setExportingAccuracyAttestationLastRun(true);
      clientService.getAccuracyAttestationLastRunCsv(
        `Accuracy Attestation Last Run_${moment.utc().local().format("yyyyMMDD_hhmmss")}`
      ).then(() => {
        setExportingAccuracyAttestationLastRun(false);
      });
    }
  }

  const handleExportClientHealthMetrics = (): void => {
    if (!exportingClientHealthMetrics) {
      setExportingClientHealthMetrics(true);
      clientService.getClientHealthMetricsCsv(
        `Client Health Metrics_${moment.utc().local().format("YYYYMMDD_HHmmss")}`
      ).then(() => {
        setExportingClientHealthMetrics(false);
      }).catch(() => {
        setExportingClientHealthMetrics(false);
      });
    }
  }

  const handleExportQuinnUtilization = (): void => {
    if (!exportingQuinnUtilization) {
      setExportingQuinnUtilization(true);
      clientService.getQuinnUtilizationCsv(
        `Quinn Utilization_${moment.utc().local().format("YYYYMMDD_HHmmss")}`
      ).then(() => {
        setExportingQuinnUtilization(false);
      }).catch(() => {
        setExportingQuinnUtilization(false);
      });
    }
  }

  const handleExportBenchmarkingCoverage = (): void => {
    if (!exportingBenchmarkingReport) {
      setExportingBenchmarkingReport(true);
      benchmarkingService.getDataCoverageCsv(
        `Medicare Advantage Data Coverage(${(userContext.roles.includes(Roles.QUEST_ADMIN) || userContext.roles.includes(Roles.QUEST_INTERNAL_REPORTS) && userContext.clientName === "Quest Analytics") ? "All" : userContext.clientName})_${moment.utc().local().format("yyyyMMDD_hhmmss")}`,
        (!userContext.roles.includes(Roles.QUEST_ADMIN) && (!userContext.roles.includes(Roles.QUEST_INTERNAL_REPORTS) || userContext.clientName !== "Quest Analytics")) ? userContext.clientId : null
      ).then(() => {
        setExportingBenchmarkingReport(false);
      });
    }
  }

  const handleExportMarketplaceCoverage = (): void => {
    if (!exportingMarketplaceCoverage) {
      setExportingMarketplaceCoverage(true);
      benchmarkingQHPService.getDataCoverageCsv(
        `Marketplace SBE Data Coverage_${moment.utc().local().format("YYYYMMDD_HHmmss")}`
      ).then(() => {
        setExportingMarketplaceCoverage(false);
      }).catch(() => {
        setExportingMarketplaceCoverage(false);
      });
    }
  }

  const handleExportMedicaidCoverage = (): void => {
    if (!exportingMedicaidCoverage) {
      setExportingMedicaidCoverage(true);
      benchmarkingMedicaidService.getDataCoverageCsv(
        `Medicaid Data Coverage_${moment.utc().local().format("YYYYMMDD_HHmmss")}`
      ).then(() => {
        setExportingMedicaidCoverage(false);
      }).catch(() => {
        setExportingMedicaidCoverage(false);
      });
    }
  }

  const tiles: TileProps[] = [
    {
      allowedRoles: [Roles.QUEST_ADMIN, Roles.CLIENT_ADMIN],
      icon: FontAwesome.List,
      route: "role-assignments",
      title: "Role Assignments",
    },
    {
      allowedRoles: [Roles.QUEST_ADMIN, Roles.CLIENT_ADMIN, Roles.QUEST_INTERNAL_REPORTS],
      classNames: exportingProjectConfigs ? "disabled" : null,
      icon: exportingProjectConfigs ? FontAwesome.Spinner : FontAwesome.Download,
      onClick: handleExportProjectConfigs,
      title: `Export ${(userContext.roles.includes(Roles.QUEST_ADMIN) || (userContext.roles.includes(Roles.QUEST_INTERNAL_REPORTS) && userContext.clientName === "Quest Analytics")) ? "Project Configuration (All Clients)" : "Client Project Configuration"}`,
    },
    {
      allowedRoles: [Roles.QUEST_ADMIN, Roles.CLIENT_ADMIN, Roles.QUEST_INTERNAL_REPORTS],
      classNames: `text-center new-line ${exportingProviderNetworkReport ? "disabled" : null}`,
      icon: exportingProviderNetworkReport ? FontAwesome.Spinner : FontAwesome.Download,
      onClick: handleExportProviderNetworkConfigs,
      title: `Export ${(userContext.roles.includes(Roles.QUEST_ADMIN) || (userContext.roles.includes(Roles.QUEST_INTERNAL_REPORTS) && userContext.clientName === "Quest Analytics")) ? "Provider Network Configuration \n (All Clients)" : "Client Provider Network Configuration"}`,
    },
    {
      allowedRoles: [Roles.QUEST_ADMIN, Roles.CLIENT_ADMIN, Roles.QUEST_INTERNAL_REPORTS],
      classNames: `text-center new-line ${exportingCountyStatusReport ? "disabled" : null}`,
      icon: exportingCountyStatusReport ? FontAwesome.Spinner : FontAwesome.Download,
      onClick: handleExportCountyStatuses,
      title: `Export ${(userContext.roles.includes(Roles.QUEST_ADMIN) || (userContext.roles.includes(Roles.QUEST_INTERNAL_REPORTS) && userContext.clientName === "Quest Analytics")) ? "Project County Statuses (All Clients)" : "Client Project County Statuses"}`,
    },
    {
      allowedRoles: [Roles.QUEST_ADMIN, Roles.CLIENT_ADMIN, Roles.QUEST_INTERNAL_REPORTS],
      classNames: `text-center new-line ${exportingBenchmarkingReport ? "disabled" : null}`,
      icon: exportingBenchmarkingReport ? FontAwesome.Spinner : FontAwesome.Download,
      onClick: handleExportBenchmarkingCoverage,
      title: `Export Competitive Insights Data Coverage for Medicare`,
    },
    {
      allowedRoles: [Roles.QUEST_ADMIN, Roles.CLIENT_ADMIN, Roles.QUEST_INTERNAL_REPORTS],
      classNames: `text-center new-line ${exportingMarketplaceCoverage ? "disabled" : null}`,
      icon: exportingMarketplaceCoverage ? FontAwesome.Spinner : FontAwesome.Download,
      onClick: handleExportMarketplaceCoverage,
      title: `Export Competitive Insights Data Coverage for Marketplace SBE`,
    },
    {
      allowedRoles: [Roles.QUEST_ADMIN, Roles.CLIENT_ADMIN, Roles.QUEST_INTERNAL_REPORTS],
      classNames: `text-center new-line ${exportingMedicaidCoverage ? "disabled" : null}`,
      icon: exportingMedicaidCoverage ? FontAwesome.Spinner : FontAwesome.Download,
      onClick: handleExportMedicaidCoverage,
      title: `Export Competitive Insights Data Coverage for Medicaid`,
    },
    {
      allowedRoles: [Roles.QUEST_INTERNAL_REPORTS],
      classNames: `text-center new-line ${exportingQasUsersByActiveClient ? "disabled" : null}`,
      icon: exportingQasUsersByActiveClient ? FontAwesome.Spinner : FontAwesome.Download,
      onClick: handleExportQasUsersByActiveClient,
      title: `QAS Users by Active Client`,
    },
    {
      allowedRoles: [Roles.QUEST_INTERNAL_REPORTS],
      classNames: `text-center new-line ${exportingInactiveClientsActiveLicenses ? "disabled" : null}`,
      icon: exportingInactiveClientsActiveLicenses ? FontAwesome.Spinner : FontAwesome.Download,
      onClick: handleExportInactiveClientsActiveLicenses,
      title: `Inactive QES Clients with Active QAS Software Licenses`,
    },
    {
      allowedRoles: [Roles.QUEST_INTERNAL_REPORTS],
      classNames: `text-center new-line ${exportingAccuracyAttestationLastRun ? "disabled" : null}`,
      icon: exportingAccuracyAttestationLastRun ? FontAwesome.Spinner : FontAwesome.Download,
      onClick: handleExportAccuracyAttestationLastRun,
      title: `Accuracy Attestation Last Run`,
    },
    {
      allowedRoles: [Roles.OPERATIONS_ADMIN],
      classNames: `text-center new-line ${exportingClientHealthMetrics ? "disabled" : ""}`,
      icon: exportingClientHealthMetrics ? FontAwesome.Spinner : FontAwesome.Download,
      onClick: handleExportClientHealthMetrics,
      title: `Export Client Health Metrics`,
    },
    {
      allowedRoles: [Roles.QUEST_INTERNAL_REPORTS],
      classNames: `text-center new-line ${exportingQuinnUtilization ? "disabled" : ""}`,
      icon: exportingQuinnUtilization ? FontAwesome.Spinner : FontAwesome.Download,
      onClick: handleExportQuinnUtilization,
      title: `Quinn Client Limits and Usage`,
    },

  ];

  return (
    <Page documentTitle="Internal Reports - Admin">
      <Breadcrumbs template={BreadcrumbTemplateEnum.InternalReports} items={[]} />
      <PageInner>
        <PageHeader iconName={FontAwesome.FilesOutline} title="Internal Reports" disableHR={false} />
        <PageSection>
          <div className="container mt-3">
            {tiles.map((tile) => <Tile key={tile.title} classNames={tile.classNames} icon={tile.icon} route={tile.route} title={tile.title} allowedRoles={tile.allowedRoles} onClick={tile.onClick} />)}
          </div>
        </PageSection>
      </PageInner>
    </Page>
  );
};

const Tile: React.FC<React.PropsWithChildren<TileProps>> = ({ classNames, icon, route = "#", onClick, title, allowedRoles }) => {
  return (
    <RoleRestricted allowedRoles={allowedRoles}>
      <Link to={`/admin/internal-reports/${route}`} className={`dashboard-tile col-4 ${classNames}`} onClick={!!onClick ? (() => onClick()) : undefined}>
        <QesIcon iconName={icon} width="26" height="26" className={icon === FontAwesome.Spinner ? "qes-icon-spin" : ""} isDecorative={true} />
        <h6>{title}</h6>
      </Link>
    </RoleRestricted>
  );
};
